// Admin → Notifications: write one message, choose who gets it, see exactly how many people each
// channel reaches, send it to yourself, then send or schedule it; and every campaign sent so far.
// The API (apps/api/src/routes/admin/notifications.ts) checks everything again: consent, content
// limits, the typed confirmation and the one-broadcast-a-day rule. Usernames shown here are what
// users chose: plain text, nothing more.
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  CircleAlert,
  Copy,
  Globe,
  Mail,
  Megaphone,
  Plus,
  Send,
  Smartphone,
  TriangleAlert,
  X,
} from 'lucide-react';
import { foreignAddress } from '@gaintplay/core/links';
import { ApiError, api } from '../lib/api';
import { useCan } from '../lib/staff';
import {
  askDetails,
  Card,
  confirmAction,
  EmptyState,
  ErrorNote,
  fmtNum,
  KV,
  PageHeader,
  Pill,
  run,
  ScrollX,
  SkeletonRows,
  TimeAgo,
  toast,
  useDebounced,
} from '../components/ui';

/* ------------------------------------------------------------------ Types */

type Kind = 'service' | 'promotion';
type Status = 'draft' | 'scheduled' | 'sending' | 'sent' | 'cancelled' | 'failed';
interface Channels {
  app: boolean;
  web: boolean;
  email: boolean;
}
type Audience =
  | { kind: 'all' }
  | { kind: 'user'; userId: string }
  | {
      kind: 'filter';
      countries?: string[];
      signedUpFrom?: number;
      signedUpTo?: number;
      minAgeDays?: number;
      maxAgeDays?: number;
      offer?: {
        providerId: string;
        offerId?: string;
        nameContains?: string;
        from?: number;
        to?: number;
      };
      cashoutsMin?: number;
      cashoutsMax?: number;
      cashedOutCentsMin?: number;
      cashedOutCentsMax?: number;
      cashedOutFrom?: number;
      cashedOutTo?: number;
      hasApp?: boolean;
      emailConfirmed?: boolean;
      balanceMin?: number;
      balanceMax?: number;
    };
type FilterAudience = Extract<Audience, { kind: 'filter' }>;

interface Limits {
  name: number;
  pushTitle: number;
  pushBody: number;
  path: number;
  emailSubject: number;
  emailBody: number;
  ctaLabel: number;
}
interface Options {
  providers: { id: string; name: string }[];
  countries: { code: string; n: number }[];
  ready: Channels;
  limits: Limits;
  typedConfirmOver: number;
  /** seesEmails: may see users' addresses (users.view); search shows them masked otherwise. */
  me: { email: string | null; owner: boolean; seesEmails: boolean };
}
interface Counts {
  matched: number;
  app: number;
  web: number;
  email: number;
  reachable: number;
}
interface Person {
  id: string;
  name: string;
}
interface Campaign {
  id: string;
  name: string;
  kind: Kind;
  status: Status;
  channels: Channels;
  push: { title: string; body: string; path: string };
  email: { subject: string; body: string; ctaLabel: string; ctaPath: string };
  audience: Audience;
  /** Went to everyone, or a filter that reaches nearly everyone (the once-a-day rule). */
  broadcast: boolean;
  /** Why a big service notice isn't marketing, as the owner who sent it wrote. */
  reason: string | null;
  scheduledAt: number | null;
  startedAt: number | null;
  finishedAt: number | null;
  recipients: {
    total: number | null;
    app: number | null;
    web: number | null;
    email: number | null;
  };
  sent: { app: number; web: number; email: number };
  failed: { app: number; web: number; email: number };
  unsubscribes: number;
  error: string | null;
  createdBy: Person | null;
  sentBy: Person | null;
  overrideBy: Person | null;
  cancelledBy: Person | null;
  cancelledAt: number | null;
  createdAt: number;
  updatedAt: number;
}
interface FoundUser {
  id: string;
  username: string;
  /** Masked ("m•••@example.com") for staff who may not see users' addresses. */
  email: string | null;
  status: string;
  emailVerified: boolean;
}
interface SendBody {
  at?: number;
  confirmRecipients?: number;
  reason?: string;
  override?: boolean;
}

/* ------------------------------------------------------------------ Shared bits */

const optionsQuery = {
  queryKey: ['notify-options'],
  queryFn: () => api<Options>('/notifications/options'),
  staleTime: 10 * 60_000,
};

const CHANNELS: { key: keyof Channels; label: string; icon: typeof Mail }[] = [
  { key: 'app', label: 'Android app', icon: Smartphone },
  { key: 'web', label: 'Browser', icon: Globe },
  { key: 'email', label: 'Email', icon: Mail },
];

const KIND_LABEL: Record<Kind, string> = { service: 'Service notice', promotion: 'Promotion' };

type Tone = NonNullable<Parameters<typeof Pill>[0]['tone']>;
const STATUS: Record<Status, { label: string; tone: Tone }> = {
  draft: { label: 'Draft', tone: 'neutral' },
  scheduled: { label: 'Scheduled', tone: 'sky' },
  sending: { label: 'Sending', tone: 'sun' },
  sent: { label: 'Sent', tone: 'mint' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
  failed: { label: 'Failed', tone: 'berry' },
};
const StatusTag = ({ s }: { s: Status }) => <Pill tone={STATUS[s].tone}>{STATUS[s].label}</Pill>;
const KindTag = ({ k }: { k: Kind }) => (
  <Pill tone={k === 'promotion' ? 'sun' : 'neutral'}>{KIND_LABEL[k]}</Pill>
);

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    return null;
  }
})();
const country = (code: string) => {
  try {
    return regionNames?.of(code) ?? code;
  } catch {
    return code;
  }
};

const pad = (n: number) => String(n).padStart(2, '0');
/** A day as the staff member's own calendar shows it (yyyy-mm-dd), and back. */
const dayOf = (t: number | undefined) => {
  if (t === undefined) return '';
  const d = new Date(t);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const startOfDay = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return s ? new Date(y!, m! - 1, d!).getTime() : undefined;
};
const endOfDay = (s: string) => {
  const [y, m, d] = s.split('-').map(Number);
  return s ? new Date(y!, m! - 1, d!, 23, 59, 59, 999).getTime() : undefined;
};
const shortDate = (t: number) =>
  new Date(t).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
const dateTime = (t: number) =>
  new Date(t).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
const whole = (s: string) => (/^\d+$/.test(s.trim()) ? Number(s.trim()) : undefined);
const cents = (s: string) =>
  /^\d+(\.\d{0,2})?$/.test(s.trim()) ? Math.round(Number(s.trim()) * 100) : undefined;
const people = (n: number) => `${fmtNum(n)} ${n === 1 ? 'person' : 'people'}`;

/** One line about who a campaign goes to, for the list and the detail page. */
function describeAudience(a: Audience, nets: Map<string, string>): string {
  if (a.kind === 'all') return 'Everyone';
  if (a.kind === 'user') return `One person (${a.userId})`;
  const range = (lo: string | undefined, hi: string | undefined, unit = '') =>
    lo && hi ? `${lo}–${hi}${unit}` : lo ? `${lo}${unit} or more` : `up to ${hi}${unit}`;
  const days = (from?: number, to?: number) =>
    from && to
      ? `${shortDate(from)} to ${shortDate(to)}`
      : from
        ? `since ${shortDate(from)}`
        : `until ${shortDate(to!)}`;
  const parts: string[] = [];
  if (a.countries?.length) parts.push(a.countries.join(', '));
  if (a.signedUpFrom || a.signedUpTo) parts.push(`signed up ${days(a.signedUpFrom, a.signedUpTo)}`);
  if (a.minAgeDays !== undefined || a.maxAgeDays !== undefined)
    parts.push(`account ${range(a.minAgeDays?.toString(), a.maxAgeDays?.toString(), ' days')} old`);
  if (a.offer)
    parts.push(
      `did ${a.offer.nameContains ? `“${a.offer.nameContains}”` : a.offer.offerId ? `offer ${a.offer.offerId}` : 'an offer'} on ${nets.get(a.offer.providerId) ?? a.offer.providerId}${a.offer.from || a.offer.to ? ` ${days(a.offer.from, a.offer.to)}` : ''}`,
    );
  if (a.cashoutsMin !== undefined || a.cashoutsMax !== undefined)
    parts.push(`${range(a.cashoutsMin?.toString(), a.cashoutsMax?.toString())} cash-outs`);
  if (a.cashedOutCentsMin !== undefined || a.cashedOutCentsMax !== undefined)
    parts.push(
      `cashed out ${range(
        a.cashedOutCentsMin !== undefined
          ? `$${(a.cashedOutCentsMin / 100).toFixed(2)}`
          : undefined,
        a.cashedOutCentsMax !== undefined
          ? `$${(a.cashedOutCentsMax / 100).toFixed(2)}`
          : undefined,
      )}`,
    );
  if (a.cashedOutFrom || a.cashedOutTo)
    parts.push(`cashed out ${days(a.cashedOutFrom, a.cashedOutTo)}`);
  if (a.hasApp !== undefined) parts.push(a.hasApp ? 'has the app' : 'no app');
  if (a.emailConfirmed !== undefined)
    parts.push(a.emailConfirmed ? 'email confirmed' : 'email not confirmed');
  if (a.balanceMin !== undefined || a.balanceMax !== undefined)
    parts.push(
      `balance ${range(a.balanceMin?.toLocaleString(), a.balanceMax?.toLocaleString(), ' coins')}`,
    );
  return parts.length ? parts.join(' · ') : 'Everyone';
}

/* ------------------------------------------------------------------ The filters, as the form holds them */

type FilterKey =
  | 'countries'
  | 'signedUp'
  | 'age'
  | 'offer'
  | 'cashouts'
  | 'cashedOut'
  | 'cashedOutWhen'
  | 'hasApp'
  | 'emailConfirmed'
  | 'balance';
interface Range {
  min: string;
  max: string;
}
interface Dates {
  from: string;
  to: string;
}
interface Filters {
  countries?: string[];
  signedUp?: Dates;
  age?: Range;
  offer?: { providerId: string; offerId: string; name: string } & Dates;
  cashouts?: Range;
  cashedOut?: Range;
  cashedOutWhen?: Dates;
  hasApp?: boolean;
  emailConfirmed?: boolean;
  balance?: Range;
}

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'countries', label: 'Country' },
  { key: 'signedUp', label: 'Signed up between' },
  { key: 'age', label: 'Account age' },
  { key: 'offer', label: 'Completed an offer' },
  { key: 'cashouts', label: 'Number of cash-outs' },
  { key: 'cashedOut', label: 'Cashed out in total (USD)' },
  { key: 'cashedOutWhen', label: 'Cashed out between' },
  { key: 'hasApp', label: 'Android app' },
  { key: 'emailConfirmed', label: 'Email confirmed' },
  { key: 'balance', label: 'Available balance' },
];

const EMPTY: Required<Filters> = {
  countries: [],
  signedUp: { from: '', to: '' },
  age: { min: '', max: '' },
  offer: { providerId: '', offerId: '', name: '', from: '', to: '' },
  cashouts: { min: '', max: '' },
  cashedOut: { min: '', max: '' },
  cashedOutWhen: { from: '', to: '' },
  hasApp: true,
  emailConfirmed: true,
  balance: { min: '', max: '' },
};

/** What the API takes. A filter added but left empty adds nothing. */
function toFilterAudience(f: Filters): FilterAudience {
  const a: FilterAudience = { kind: 'filter' };
  const put = <K extends keyof FilterAudience>(k: K, v: FilterAudience[K] | undefined) => {
    if (v !== undefined) a[k] = v;
  };
  if (f.countries?.length) a.countries = f.countries;
  if (f.signedUp) {
    put('signedUpFrom', startOfDay(f.signedUp.from));
    put('signedUpTo', endOfDay(f.signedUp.to));
  }
  if (f.age) {
    put('minAgeDays', whole(f.age.min));
    put('maxAgeDays', whole(f.age.max));
  }
  if (f.offer?.providerId) {
    const o: NonNullable<FilterAudience['offer']> = { providerId: f.offer.providerId };
    if (f.offer.offerId.trim()) o.offerId = f.offer.offerId.trim();
    if (f.offer.name.trim()) o.nameContains = f.offer.name.trim();
    const from = startOfDay(f.offer.from);
    const to = endOfDay(f.offer.to);
    if (from !== undefined) o.from = from;
    if (to !== undefined) o.to = to;
    a.offer = o;
  }
  if (f.cashouts) {
    put('cashoutsMin', whole(f.cashouts.min));
    put('cashoutsMax', whole(f.cashouts.max));
  }
  if (f.cashedOut) {
    put('cashedOutCentsMin', cents(f.cashedOut.min));
    put('cashedOutCentsMax', cents(f.cashedOut.max));
  }
  if (f.cashedOutWhen) {
    put('cashedOutFrom', startOfDay(f.cashedOutWhen.from));
    put('cashedOutTo', endOfDay(f.cashedOutWhen.to));
  }
  if (f.hasApp !== undefined) a.hasApp = f.hasApp;
  if (f.emailConfirmed !== undefined) a.emailConfirmed = f.emailConfirmed;
  if (f.balance) {
    put('balanceMin', whole(f.balance.min));
    put('balanceMax', whole(f.balance.max));
  }
  return a;
}

/** A saved audience back into the form. */
function filtersFrom(a: FilterAudience): Filters {
  const f: Filters = {};
  const n = (v: number | undefined) => (v === undefined ? '' : String(v));
  const usd = (v: number | undefined) => (v === undefined ? '' : (v / 100).toFixed(2));
  if (a.countries?.length) f.countries = a.countries;
  if (a.signedUpFrom !== undefined || a.signedUpTo !== undefined)
    f.signedUp = { from: dayOf(a.signedUpFrom), to: dayOf(a.signedUpTo) };
  if (a.minAgeDays !== undefined || a.maxAgeDays !== undefined)
    f.age = { min: n(a.minAgeDays), max: n(a.maxAgeDays) };
  if (a.offer)
    f.offer = {
      providerId: a.offer.providerId,
      offerId: a.offer.offerId ?? '',
      name: a.offer.nameContains ?? '',
      from: dayOf(a.offer.from),
      to: dayOf(a.offer.to),
    };
  if (a.cashoutsMin !== undefined || a.cashoutsMax !== undefined)
    f.cashouts = { min: n(a.cashoutsMin), max: n(a.cashoutsMax) };
  if (a.cashedOutCentsMin !== undefined || a.cashedOutCentsMax !== undefined)
    f.cashedOut = { min: usd(a.cashedOutCentsMin), max: usd(a.cashedOutCentsMax) };
  if (a.cashedOutFrom !== undefined || a.cashedOutTo !== undefined)
    f.cashedOutWhen = { from: dayOf(a.cashedOutFrom), to: dayOf(a.cashedOutTo) };
  if (a.hasApp !== undefined) f.hasApp = a.hasApp;
  if (a.emailConfirmed !== undefined) f.emailConfirmed = a.emailConfirmed;
  if (a.balanceMin !== undefined || a.balanceMax !== undefined)
    f.balance = { min: n(a.balanceMin), max: n(a.balanceMax) };
  return f;
}

/* ------------------------------------------------------------------ History */

export function Notifications() {
  const can = useCan();
  const { data: options } = useQuery({ ...optionsQuery, enabled: can('notifications.send') });
  const list = useInfiniteQuery({
    queryKey: ['notify-campaigns'],
    queryFn: ({ pageParam }) =>
      api<{ campaigns: Campaign[]; next: number | null }>(
        `/notifications/campaigns${pageParam ? `?before=${pageParam}` : ''}`,
      ),
    initialPageParam: 0,
    getNextPageParam: (last) => last.next ?? undefined,
    enabled: can('notifications.send'),
    refetchInterval: (q) =>
      q.state.data?.pages.some((p) => p.campaigns.some((c) => c.status === 'sending'))
        ? 15_000
        : 60_000,
  });
  const nets = useMemo(
    () => new Map((options?.providers ?? []).map((p) => [p.id, p.name])),
    [options],
  );
  const header = (
    <PageHeader
      title="Notifications"
      description="Messages to many users at once: push to the Android app and browsers, and email. Service notices reach everyone who can get them; promotions only reach people who asked for offers."
      actions={
        can('notifications.send') ? (
          <Link to="/notifications/new" className="btn-primary h-10 py-0">
            <Plus size={16} aria-hidden="true" /> New campaign
          </Link>
        ) : undefined
      }
    />
  );
  if (!can('notifications.send'))
    return (
      <div className="space-y-5">
        {header}
        <Card>
          <EmptyState icon={Megaphone} title="You can’t send notifications">
            Sending to users needs the “Send notifications and emails to users” permission. An owner
            can add it to your role.
          </EmptyState>
        </Card>
      </div>
    );
  const rows = list.data?.pages.flatMap((p) => p.campaigns) ?? [];
  return (
    <div className="space-y-5">
      {header}
      {list.error && !list.data && (
        <ErrorNote error={list.error} onRetry={() => void list.refetch()} />
      )}
      <Card pad={false}>
        {!list.data && !list.error ? (
          <SkeletonRows rows={5} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title="Nothing sent yet"
            action={
              <Link to="/notifications/new" className="btn-primary">
                <Plus size={16} aria-hidden="true" /> New campaign
              </Link>
            }
          >
            Write a notice or a promotion, check who it reaches, send it to yourself, then to them.
          </EmptyState>
        ) : (
          <ScrollX>
            <table className="data-table table-compact table-baseline min-w-[860px]">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Status</th>
                  <th>Audience</th>
                  <th className="text-right">Delivered</th>
                  <th>When</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <HistoryRow key={c.id} c={c} nets={nets} />
                ))}
              </tbody>
            </table>
          </ScrollX>
        )}
      </Card>
      {list.hasNextPage && (
        <div className="flex justify-center">
          <button type="button" className="btn-ghost" onClick={() => void list.fetchNextPage()}>
            Show older campaigns
          </button>
        </div>
      )}
    </div>
  );
}

function HistoryRow({ c, nets }: { c: Campaign; nets: Map<string, string> }) {
  const navigate = useNavigate();
  const delivered = c.sent.app + c.sent.web + c.sent.email;
  const failed = c.failed.app + c.failed.web + c.failed.email;
  const when =
    c.status === 'scheduled'
      ? c.scheduledAt
      : c.status === 'draft'
        ? c.updatedAt
        : (c.startedAt ?? c.cancelledAt ?? c.updatedAt);
  return (
    <tr className="cursor-pointer" onClick={() => void navigate(`/notifications/${c.id}`)}>
      <td className="max-w-[300px]">
        <Link
          to={`/notifications/${c.id}`}
          onClick={(e) => e.stopPropagation()}
          className="block truncate font-bold hover:underline hover:decoration-coin hover:decoration-2 hover:underline-offset-2"
        >
          {c.name}
        </Link>
        <span className="mt-1 flex items-center gap-2 text-[12px] text-stone">
          <KindTag k={c.kind} />
          <ChannelIcons ch={c.channels} />
        </span>
      </td>
      <td>
        <StatusTag s={c.status} />
      </td>
      <td
        className="max-w-[260px] truncate text-graphite"
        title={describeAudience(c.audience, nets)}
      >
        {describeAudience(c.audience, nets)}
      </td>
      <td className="text-right tabular-nums">
        {c.status === 'draft' || c.status === 'scheduled' || delivered + failed === 0 ? (
          <span className="text-stone">—</span>
        ) : (
          <>
            <b>{fmtNum(delivered)}</b>
            {failed > 0 && <span className="text-berry-deep"> · {fmtNum(failed)} failed</span>}
          </>
        )}
      </td>
      <td className="whitespace-nowrap">
        {c.status === 'scheduled' && when ? dateTime(when) : <TimeAgo t={when} />}
      </td>
      <td className="whitespace-nowrap text-graphite">{(c.sentBy ?? c.createdBy)?.name ?? '—'}</td>
    </tr>
  );
}

function ChannelIcons({ ch }: { ch: Channels }) {
  return (
    <span className="inline-flex items-center gap-1.5" aria-label={channelNames(ch)}>
      {CHANNELS.filter((x) => ch[x.key]).map((x) => (
        <x.icon key={x.key} size={14} strokeWidth={1.75} aria-hidden="true" />
      ))}
    </span>
  );
}
const channelNames = (ch: Channels) =>
  CHANNELS.filter((x) => ch[x.key])
    .map((x) => x.label)
    .join(', ') || 'No channel';

/* ------------------------------------------------------------------ One campaign */

interface Failure {
  channel: string;
  code: string;
  n: number;
}
interface CampaignDetail {
  campaign: Campaign;
  failures: Failure[];
  problem: string | null;
}

/** The reason codes the sender stores, in words; anything else shows as the code. */
const FAILURE_LABEL: Record<string, string> = {
  suppressed: 'Address bounced before or reported us as spam',
  interrupted: 'Cut off mid-send (not sent again, in case it arrived)',
  fcm_unregistered: 'App uninstalled or token expired',
  web_gone: 'Browser stopped accepting notifications',
  no_phone: 'No phone left',
  no_browser: 'No browser left',
  push_not_configured: 'Android push isn’t set up',
  fcm_auth: 'Couldn’t sign in to Firebase',
  fcm_timeout: 'Firebase didn’t answer in time',
  web_timeout: 'Push service didn’t answer in time',
  ses_timeout: 'Amazon SES didn’t answer in time (it may have sent it)',
  ses_network: 'Couldn’t reach Amazon SES',
  no_provider: 'No email provider set up',
  internal: 'Our error',
  not_sent: 'Not sent',
};
const campaignQuery = (id: string) => ({
  queryKey: ['notify-campaign', id],
  queryFn: () => api<CampaignDetail>(`/notifications/campaigns/${id}`),
});

export function NotificationCampaign() {
  const { id = 'new' } = useParams();
  const can = useCan();
  const options = useQuery({ ...optionsQuery, enabled: can('notifications.send') });
  const isNew = id === 'new';
  // A new campaign gets its id on the first save; the composer stays the same one meanwhile.
  const began = useRef(id);
  const detail = useQuery({
    ...campaignQuery(id),
    enabled: !isNew && can('notifications.send'),
    refetchInterval: (q) => {
      const s = q.state.data?.campaign.status;
      return s === 'sending' ? 10_000 : s === 'scheduled' ? 30_000 : false;
    },
  });
  if (!can('notifications.send')) return <Notifications />;
  const err = options.error ?? detail.error;
  if (err)
    return (
      <div className="space-y-5">
        <BackLink />
        <ErrorNote error={err} onRetry={() => void (options.refetch(), detail.refetch())} />
      </div>
    );
  if (!options.data || (!isNew && !detail.data))
    return (
      <div className="space-y-5" aria-busy="true">
        <BackLink />
        <div className="h-9 w-64 animate-pulse rounded-[10px] bg-ink/[0.07]" />
        <Card pad={false}>
          <SkeletonRows rows={4} />
        </Card>
      </div>
    );
  const c = detail.data?.campaign;
  if (!c || c.status === 'draft')
    return (
      <Composer
        key={began.current === 'new' ? 'new' : (c?.id ?? 'new')}
        options={options.data}
        saved={c ?? null}
      />
    );
  return <Detail c={c} failures={detail.data!.failures} options={options.data} />;
}

function BackLink() {
  return (
    <Link
      to="/notifications"
      className="inline-flex min-h-9 items-center text-[13px] font-semibold text-stone hover:text-ink"
    >
      ← All campaigns
    </Link>
  );
}

/* ------------------------------------------------------------------ Composer */

interface Form {
  name: string;
  kind: Kind;
  channels: Channels;
  push: { title: string; body: string; path: string };
  email: { subject: string; body: string; ctaLabel: string; ctaPath: string };
  mode: 'all' | 'user' | 'filter';
  user: { id: string; label: string } | null;
  filters: Filters;
}

function formFrom(c: Campaign | null): Form {
  if (!c)
    return {
      name: '',
      kind: 'service',
      channels: { app: true, web: true, email: false },
      push: { title: '', body: '', path: '' },
      email: { subject: '', body: '', ctaLabel: '', ctaPath: '' },
      mode: 'filter',
      user: null,
      filters: {},
    };
  const a = c.audience;
  return {
    name: c.name,
    kind: c.kind,
    channels: c.channels,
    push: c.push,
    email: c.email,
    mode: a.kind,
    user: a.kind === 'user' ? { id: a.userId, label: a.userId } : null,
    filters: a.kind === 'filter' ? filtersFrom(a) : {},
  };
}

function audienceOf(f: Form): Audience | null {
  if (f.mode === 'all') return { kind: 'all' };
  if (f.mode === 'user') return f.user ? { kind: 'user', userId: f.user.id } : null;
  return toFilterAudience(f.filters);
}

const bodyOf = (f: Form) => ({
  name: f.name.trim() || 'Untitled campaign',
  kind: f.kind,
  channels: f.channels,
  push: f.push,
  email: f.email,
  audience: audienceOf(f) ?? { kind: 'filter' as const },
});

/** What still stops it from going out, in the composer's words (the API says the same). */
function missing(f: Form): string | null {
  const ch = f.channels;
  if (!ch.app && !ch.web && !ch.email) return 'Pick at least one channel.';
  if ((ch.app || ch.web) && (!f.push.title.trim() || !f.push.body.trim()))
    return 'Write the notification’s title and text.';
  if (ch.email && (!f.email.subject.trim() || !f.email.body.trim()))
    return 'Write the email’s subject and text.';
  if (!!f.email.ctaLabel.trim() !== !!f.email.ctaPath.trim())
    return 'Give the email button both a label and a page.';
  if (f.mode === 'user' && !f.user) return 'Pick the person to send it to.';
  return null;
}

/**
 * A link, bare domain or email address that isn't GaintPlay's (core's host-based rule), or null.
 * The same rule as the API's (apps/api/src/lib/campaigns.ts foreignLink), so the composer says it
 * before saving does.
 */
const foreignLink = (s: string): string | null =>
  foreignAddress(s) ?? (/\bhttps?:(?!\/\/[^\s<>"])/i.test(s) ? 'http' : null);
const SITE_PATH = (p: string) =>
  p === '' ||
  (/^\/[A-Za-z0-9\-._~/?=&%#+]*$/.test(p) &&
    !p.includes('//') &&
    !/(^|\/)\.\.?(\/|$|\?|#)/.test(p));

function Composer({ options, saved }: { options: Options; saved: Campaign | null }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [f, setF] = useState<Form>(() => formFrom(saved));
  const [id, setId] = useState<string | null>(saved?.id ?? null);
  const [savedJson, setSavedJson] = useState(() =>
    saved ? JSON.stringify(bodyOf(formFrom(saved))) : '',
  );
  const [when, setWhen] = useState<'now' | 'later'>(saved?.scheduledAt ? 'later' : 'now');
  // Why a schedule came back to draft (the audience grew); gone once it's saved again.
  const [notice, setNotice] = useState(saved?.error ?? null);
  const [at, setAt] = useState(() => {
    const d = new Date(Date.now() + 2 * 3600_000);
    d.setMinutes(0, 0, 0);
    return `${dayOf(d.getTime())}T${pad(d.getHours())}:00`;
  });
  const L = options.limits;
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const audience = audienceOf(f);
  const anyChannel = f.channels.app || f.channels.web || f.channels.email;

  // Who it reaches, a moment after the last change.
  const countKey = useDebounced(
    JSON.stringify({ kind: f.kind, channels: f.channels, audience }),
    400,
  );
  const reach = useQuery({
    queryKey: ['notify-audience', countKey],
    queryFn: () =>
      api<{ counts: Counts; samples: string[]; everyone: boolean }>('/notifications/audience', {
        body: JSON.parse(countKey) as unknown,
      }),
    enabled: !!audience,
    placeholderData: keepPreviousData,
  });

  // Fill in the name of a saved one-person audience.
  useEffect(() => {
    if (f.mode !== 'user' || !f.user || f.user.label !== f.user.id) return;
    const want = f.user.id;
    void api<{ users: FoundUser[] }>(`/notifications/users?q=${encodeURIComponent(want)}`)
      .then((r) => {
        const u = r.users.find((x) => x.id === want);
        if (u)
          setF((x) => (x.user?.id === want ? { ...x, user: { id: want, label: u.username } } : x));
      })
      .catch(() => {});
  }, [f.mode, f.user]);

  const problems: string[] = [];
  const textProblem = (label: string, s: string) => {
    const found = foreignLink(s);
    if (found)
      problems.push(
        `${label}: no links, websites or email addresses except GaintPlay’s${found === 'http' ? '' : ` (“${found}”)`}. Use the link field.`,
      );
  };
  if (f.channels.app || f.channels.web) {
    textProblem('Notification title', f.push.title);
    textProblem('Notification text', f.push.body);
    if (!SITE_PATH(f.push.path.trim()))
      problems.push('Notification link: a page on GaintPlay, like /earn.');
  }
  if (f.channels.email) {
    textProblem('Email subject', f.email.subject);
    textProblem('Email text', f.email.body);
    textProblem('Button label', f.email.ctaLabel);
    if (!SITE_PATH(f.email.ctaPath.trim()))
      problems.push('Button page: a page on GaintPlay, like /earn.');
  }
  const blocker = problems[0] ?? missing(f);
  const dirty = JSON.stringify(bodyOf(f)) !== savedJson;

  /** Save the draft (create it the first time); resolves to its id. */
  async function save(quiet = false): Promise<string | null> {
    const body = bodyOf(f);
    const r = await run(
      async () => {
        if (id) {
          await api(`/notifications/campaigns/${id}`, { method: 'PUT', body });
          return id;
        }
        const made = await api<{ id: string }>('/notifications/campaigns', { body });
        return made.id;
      },
      quiet ? undefined : 'Draft saved.',
    );
    if (!r) return null;
    setSavedJson(JSON.stringify(body));
    setNotice(null);
    if (!id) {
      setId(r);
      // Loaded before the address changes, so the page doesn't blink back to loading.
      await qc.prefetchQuery(campaignQuery(r));
      void navigate(`/notifications/${r}`, { replace: true });
    }
    void qc.invalidateQueries({ queryKey: ['notify-campaigns'] });
    return r;
  }

  async function test(pushUserId: string | null) {
    const saved = await save(true);
    if (!saved) return;
    const r = await run(() =>
      api<{
        email: { to: string; ok: boolean; error?: string } | null;
        push: { ok: true; app: boolean; web: boolean } | { ok: false; error: string } | null;
      }>(`/notifications/campaigns/${saved}/test`, { body: pushUserId ? { pushUserId } : {} }),
    );
    if (!r) return;
    const said: string[] = [];
    if (r.email)
      said.push(
        r.email.ok ? `Email sent to ${r.email.to}.` : `Email failed: ${r.email.error ?? ''}`,
      );
    if (r.push)
      said.push(
        r.push.ok
          ? `Push sent to its ${[r.push.app && 'phone', r.push.web && 'browser'].filter(Boolean).join(' and ')}.`
          : `Push not sent: ${r.push.error}`,
      );
    const bad = (r.email && !r.email.ok) || (r.push && !r.push.ok);
    toast(said.join(' '), bad ? 'error' : 'ok');
  }

  // A service notice skips the offers opt-in: to this many people, only an owner sends one, with
  // a written reason (the API enforces both).
  const big =
    !!reach.data && (reach.data.everyone || reach.data.counts.reachable > options.typedConfirmOver);
  const ownerOnly = f.kind === 'service' && big;

  async function send() {
    const scheduleAt = when === 'later' ? new Date(at).getTime() : undefined;
    if (scheduleAt !== undefined && !(scheduleAt > Date.now() + 60_000)) {
      toast('Pick a time at least a minute from now.', 'error');
      return;
    }
    const saved = await save(true);
    if (!saved) return;
    let fresh: { counts: Counts; everyone: boolean } | undefined;
    try {
      fresh = await api<{ counts: Counts; samples: string[]; everyone: boolean }>(
        '/notifications/audience',
        { body: { kind: f.kind, channels: f.channels, audience } },
      );
    } catch (e) {
      toast((e as Error).message, 'error');
      return;
    }
    const n = fresh.counts.reachable;
    const typed = fresh.everyone || n > options.typedConfirmOver;
    const needsReason = f.kind === 'service' && typed;
    if (needsReason && !options.me.owner) {
      toast(
        'A service notice to this many people skips the offers opt-in, so an owner has to send it.',
        'error',
      );
      return;
    }
    const answer = await askDetails({
      eyebrow: `${KIND_LABEL[f.kind]}${fresh.everyone ? ' to everyone' : ''}`,
      title: scheduleAt ? `Schedule for ${dateTime(scheduleAt)}?` : `Send to ${people(n)} now?`,
      body: (
        <ConfirmBody counts={fresh.counts} ch={f.channels} kind={f.kind} later={!!scheduleAt} />
      ),
      confirmLabel: scheduleAt ? 'Schedule it' : `Send to ${fmtNum(n)}`,
      ...(typed
        ? {
            typed: {
              label: `Type ${fmtNum(n)} to confirm`,
              expect: String(n),
              hint: fresh.everyone
                ? 'It goes to everyone, so it needs the number typed back.'
                : `More than ${fmtNum(options.typedConfirmOver)} people, so it needs the number typed back.`,
            },
          }
        : {}),
      ...(needsReason
        ? {
            reason: {
              label: 'Why is this a service notice, not marketing?',
              placeholder:
                'e.g. Planned maintenance: every account is offline for 10 minutes tonight.',
              min: 10,
            },
          }
        : {}),
    });
    if (!answer) return;
    await post(saved, scheduleAt, {
      ...(scheduleAt ? { at: scheduleAt } : {}),
      // What staff typed, not the count fetched: the API checks it against the audience now.
      ...(typed ? { confirmRecipients: Number(answer.typed) } : {}),
      ...(needsReason ? { reason: answer.reason } : {}),
    });
  }

  async function post(saved: string, scheduleAt: number | undefined, body: SendBody) {
    try {
      const r = await api<{ status: Status }>(`/notifications/campaigns/${saved}/send`, { body });
      toast(r.status === 'scheduled' ? `Scheduled for ${dateTime(scheduleAt!)}.` : 'Sending now.');
      void qc.invalidateQueries({ queryKey: ['notify-campaigns'] });
      await qc.invalidateQueries({ queryKey: ['notify-campaign', saved] });
      void navigate(`/notifications/${saved}`, { replace: true });
    } catch (e) {
      const ae = e instanceof ApiError ? e : null;
      if (ae?.code === 'EVERYONE_LIMIT' && options.me.owner && !body.override) {
        const other = ae.data?.other as { name: string; at: number } | undefined;
        const yes = await confirmAction({
          eyebrow: 'Owner override',
          title: 'Send a second campaign to everyone today?',
          tone: 'danger',
          body: (
            <p>
              “{other?.name}” goes to everyone (or nearly){' '}
              {other ? `on ${dateTime(other.at)}` : 'within a day'}. Two broadcasts in a day annoy
              people and get emails marked as spam. Only do this for something urgent; it’s recorded
              with your name.
            </p>
          ),
          confirmLabel: 'Send anyway',
        });
        if (yes !== null) await post(saved, scheduleAt, { ...body, override: true });
        return;
      }
      // Service notices beyond the day's limit (many small ones add up): an owner says why.
      if (ae?.code === 'REASON_NEEDED' && options.me.owner && !body.reason) {
        const answer = await askDetails({
          eyebrow: 'Service notice',
          title: 'Why is this a service notice?',
          body: <p>{ae.message}</p>,
          confirmLabel: scheduleAt ? 'Schedule it' : 'Send',
          reason: {
            label: 'Why is this a service notice, not marketing?',
            placeholder:
              'e.g. Planned maintenance: every account is offline for 10 minutes tonight.',
            min: 10,
          },
        });
        if (answer) await post(saved, scheduleAt, { ...body, reason: answer.reason });
        return;
      }
      if (ae?.code === 'CONFIRM') void reach.refetch();
      toast((e as Error).message, 'error');
    }
  }

  return (
    <div className="space-y-5">
      <BackLink />
      <PageHeader
        eyebrow={id ? 'Draft' : 'New campaign'}
        title={f.name.trim() || 'New campaign'}
        description="Write it once; it goes out on every channel you pick. Nothing is sent until you confirm."
      />
      {notice && (
        <div
          role="status"
          className="flex items-start gap-2.5 rounded-[14px] border-[1.5px] border-ink bg-sun/25 px-4 py-3 text-[14px] leading-snug"
        >
          <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{notice}</span>
        </div>
      )}
      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-5">
          <Card title="Message">
            <div className="space-y-5">
              <TextField
                label="Name"
                hint="Only staff see it, in this list."
                value={f.name}
                max={L.name}
                onChange={(v) => set('name', v)}
                placeholder="e.g. Maintenance 24 Oct"
              />
              <KindPicker kind={f.kind} onChange={(k) => set('kind', k)} />
              <fieldset>
                <legend className="label">Send it as</legend>
                <div className="grid gap-2">
                  {CHANNELS.map((x) => (
                    <ChannelTile
                      key={x.key}
                      icon={x.icon}
                      label={x.label}
                      on={f.channels[x.key]}
                      ready={options.ready[x.key]}
                      count={reach.data?.counts[x.key]}
                      sub={
                        x.key === 'email'
                          ? f.kind === 'promotion'
                            ? 'Confirmed, asked for offers'
                            : 'Confirmed addresses'
                          : f.kind === 'promotion'
                            ? 'Notifications on, asked for offers'
                            : 'Notifications on'
                      }
                      onChange={(on) => set('channels', { ...f.channels, [x.key]: on })}
                    />
                  ))}
                </div>
              </fieldset>
            </div>
          </Card>

          {(f.channels.app || f.channels.web) && (
            <Card title="Push notification">
              <div className="space-y-5">
                <div className="space-y-4">
                  <TextField
                    label="Title"
                    value={f.push.title}
                    max={L.pushTitle}
                    onChange={(v) => set('push', { ...f.push, title: v })}
                    placeholder="Double coins on surveys today"
                  />
                  <TextField
                    label="Text"
                    value={f.push.body}
                    max={L.pushBody}
                    rows={3}
                    onChange={(v) => set('push', { ...f.push, body: v })}
                    placeholder="Every survey pays twice the coins until midnight UTC."
                  />
                  <TextField
                    label="Opens"
                    hint="A page on GaintPlay, like /earn or /wallet. Browsers open it; the Android app opens its start page."
                    value={f.push.path}
                    max={L.path}
                    mono
                    onChange={(v) => set('push', { ...f.push, path: v })}
                    placeholder="/earn"
                  />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {f.channels.app && <PhonePreview title={f.push.title} body={f.push.body} />}
                  {f.channels.web && (
                    <BrowserPreview title={f.push.title} body={f.push.body} path={f.push.path} />
                  )}
                </div>
              </div>
            </Card>
          )}

          {f.channels.email && (
            <Card title="Email">
              <div className="space-y-4">
                <TextField
                  label="Subject"
                  hint="Under 60 characters shows in full on phones."
                  value={f.email.subject}
                  max={L.emailSubject}
                  onChange={(v) => set('email', { ...f.email, subject: v })}
                  placeholder="We’re updating GaintPlay tonight"
                />
                <TextField
                  label="Text"
                  hint="Plain text. A blank line starts a paragraph, a line starting “- ” is a bullet, **two stars** make bold. No links: use the button."
                  value={f.email.body}
                  max={L.emailBody}
                  rows={8}
                  onChange={(v) => set('email', { ...f.email, body: v })}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <TextField
                    label="Button label (optional)"
                    value={f.email.ctaLabel}
                    max={L.ctaLabel}
                    onChange={(v) => set('email', { ...f.email, ctaLabel: v })}
                    placeholder="Start earning"
                  />
                  <TextField
                    label="Button opens"
                    value={f.email.ctaPath}
                    max={L.path}
                    mono
                    onChange={(v) => set('email', { ...f.email, ctaPath: v })}
                    placeholder="/earn"
                  />
                </div>
                <EmailPreview kind={f.kind} email={f.email} />
              </div>
            </Card>
          )}

          <Card title="Audience">
            <AudienceEditor f={f} setF={setF} options={options} />
          </Card>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-6">
          <ReachCard
            counts={reach.data?.counts}
            samples={reach.data?.samples ?? []}
            everyone={!!reach.data?.everyone}
            literal={audience?.kind === 'all'}
            loading={reach.isFetching}
            error={reach.error}
            ch={f.channels}
            kind={f.kind}
            waiting={!audience}
          />
          <Card>
            <div className="space-y-4">
              <div>
                <div className="label">When</div>
                <div
                  className="inline-flex rounded-[12px] bg-ink/[0.06] p-1"
                  role="radiogroup"
                  aria-label="When"
                >
                  {(['now', 'later'] as const).map((w) => (
                    <button
                      key={w}
                      type="button"
                      role="radio"
                      aria-checked={when === w}
                      onClick={() => setWhen(w)}
                      className={`h-9 rounded-[9px] px-4 text-[14px] font-semibold transition max-sm:h-10 ${
                        when === w
                          ? 'bg-paper text-ink shadow-[0_1px_0_#e2ddd2,0_1px_3px_rgba(17,17,16,0.08)]'
                          : 'text-graphite hover:text-ink'
                      }`}
                    >
                      {w === 'now' ? 'Send now' : 'Schedule'}
                    </button>
                  ))}
                </div>
                {when === 'later' && (
                  <div className="mt-3">
                    <input
                      type="datetime-local"
                      aria-label="Send at"
                      className="input"
                      value={at}
                      min={`${dayOf(Date.now())}T00:00`}
                      onChange={(e) => setAt(e.target.value)}
                    />
                    <p className="mt-1.5 text-[12px] text-stone">
                      Your time zone. It goes out within 5 minutes of this time.
                    </p>
                  </div>
                )}
              </div>
              {blocker && (
                <p className="flex items-start gap-2 rounded-[10px] bg-sun/25 px-3 py-2 text-[13px] font-semibold text-ink">
                  <CircleAlert size={15} className="mt-px shrink-0" aria-hidden="true" />
                  {blocker}
                </p>
              )}
              {ownerOnly && (
                <p
                  className={`flex items-start gap-2 rounded-[10px] px-3 py-2 text-[13px] leading-snug ${
                    options.me.owner ? 'bg-sun/25 text-ink' : 'bg-berry/15 text-berry-deep'
                  }`}
                >
                  <TriangleAlert size={15} className="mt-0.5 shrink-0" aria-hidden="true" />
                  <span>
                    <b className="font-semibold">A service notice skips the offers opt-in.</b>{' '}
                    {options.me.owner
                      ? 'To this many people, you’ll write why it isn’t marketing; it’s kept with the campaign and in the audit log. Nothing that sells, ever.'
                      : 'To this many people only an owner can send one. Ask an owner, or make it a promotion.'}
                  </span>
                </p>
              )}
              <div className="grid gap-2">
                <button
                  type="button"
                  className="btn-primary w-full"
                  disabled={
                    !!blocker ||
                    !anyChannel ||
                    !reach.data?.counts.reachable ||
                    (ownerOnly && !options.me.owner)
                  }
                  onClick={() => void send()}
                >
                  <Send size={16} aria-hidden="true" />
                  {when === 'later'
                    ? 'Schedule…'
                    : reach.data?.counts.reachable
                      ? `Send to ${people(reach.data.counts.reachable)}…`
                      : 'Send…'}
                </button>
                <button
                  type="button"
                  className="btn-ghost w-full"
                  disabled={!!id && !dirty}
                  onClick={() => void save()}
                >
                  {id && !dirty ? 'Draft saved' : 'Save draft'}
                </button>
              </div>
            </div>
          </Card>
          <TestPanel f={f} me={options.me.email} disabled={!!blocker} onSend={test} />
        </aside>
      </div>
    </div>
  );
}

function ConfirmBody({
  counts,
  ch,
  kind,
  later,
}: {
  counts: Counts;
  ch: Channels;
  kind: Kind;
  later: boolean;
}) {
  return (
    <div className="space-y-3">
      <ul className="divide-y divide-hairline overflow-hidden rounded-[12px] border border-hairline bg-paper text-[14px]">
        {CHANNELS.filter((x) => ch[x.key]).map((x) => (
          <li key={x.key} className="flex items-center gap-2.5 px-3.5 py-2.5">
            <x.icon size={15} strokeWidth={1.75} aria-hidden="true" className="text-stone" />
            {x.label}
            <b className="ml-auto tabular-nums">{fmtNum(counts[x.key])}</b>
          </li>
        ))}
        <li className="flex items-center gap-2.5 bg-canvas/60 px-3.5 py-2.5 font-semibold">
          People in total
          <b className="ml-auto tabular-nums">{fmtNum(counts.reachable)}</b>
        </li>
      </ul>
      <p className="text-[13px] text-graphite">
        {kind === 'promotion'
          ? 'A promotion: only people who asked for offers get it.'
          : 'A service notice: it must be about their account or GaintPlay, not an offer.'}{' '}
        {later
          ? 'Counted again when it goes out. You can cancel it until then.'
          : 'Once it starts you can stop the rest, not take back what went out.'}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ Composer parts */

function TextField({
  label,
  hint,
  value,
  max,
  rows,
  mono = false,
  placeholder,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  max: number;
  rows?: number;
  mono?: boolean;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  const over = value.length > max;
  const near = value.length > max * 0.85;
  const common = {
    id,
    value,
    placeholder,
    'aria-describedby': `${id}-n${hint ? ` ${id}-h` : ''}`,
    'aria-invalid': over || undefined,
    onChange: (e: { target: { value: string } }) => onChange(e.target.value),
    className: `input ${mono ? 'font-mono text-[14px]' : ''} ${rows ? 'resize-y leading-relaxed' : ''}`,
  };
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="label">
          {label}
        </label>
        <span
          id={`${id}-n`}
          className={`text-[12px] tabular-nums ${over ? 'font-bold text-berry-deep' : near ? 'text-graphite' : 'text-stone'}`}
        >
          {value.length}/{max}
        </span>
      </div>
      {rows ? <textarea rows={rows} {...common} /> : <input {...common} />}
      {hint && (
        <p id={`${id}-h`} className="mt-1.5 text-[12px] leading-snug text-stone">
          {hint}
        </p>
      )}
    </div>
  );
}

function KindPicker({ kind, onChange }: { kind: Kind; onChange: (k: Kind) => void }) {
  const items: { k: Kind; title: string; text: string }[] = [
    {
      k: 'service',
      title: 'Service notice',
      text: 'Something people need to know about their account or GaintPlay: maintenance, a rule change, a problem we fixed. Reaches every confirmed email and everyone with notifications on, so it must never sell anything. To more than 1,000 people, only an owner sends one, and says why.',
    },
    {
      k: 'promotion',
      title: 'Promotion',
      text: 'Offers, bonuses, anything that sells. Only reaches people who asked for offers on their Account page; every email has a one-click unsubscribe.',
    },
  ];
  return (
    <fieldset>
      <legend className="label">Type</legend>
      <div className="grid gap-2 sm:grid-cols-2" role="radiogroup">
        {items.map((i) => {
          const on = kind === i.k;
          return (
            <button
              key={i.k}
              type="button"
              role="radio"
              aria-checked={on}
              onClick={() => onChange(i.k)}
              className={`flex flex-col items-start justify-start rounded-[12px] border p-3.5 text-left transition ${
                on ? 'border-ink bg-canvas/60' : 'border-hairline hover:border-ink'
              }`}
            >
              <span className="flex items-center gap-2 text-[15px] font-bold">
                <span
                  aria-hidden="true"
                  className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border-[1.5px] ${on ? 'border-ink bg-ink' : 'border-[#cfc8ba]'}`}
                >
                  {on && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                </span>
                {i.title}
              </span>
              <span className="mt-1.5 block text-[13px] leading-snug text-graphite">{i.text}</span>
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

function ChannelTile({
  icon: Icon,
  label,
  sub,
  on,
  ready,
  count,
  onChange,
}: {
  icon: typeof Mail;
  label: string;
  sub: string;
  on: boolean;
  ready: boolean;
  count: number | undefined;
  onChange: (on: boolean) => void;
}) {
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded-[12px] border px-3.5 py-3 transition ${
        !ready
          ? 'cursor-not-allowed opacity-60'
          : on
            ? 'border-ink bg-canvas/60'
            : 'border-hairline hover:border-ink'
      }`}
    >
      <input
        type="checkbox"
        className="h-[18px] w-[18px] shrink-0 cursor-pointer accent-ink"
        checked={on && ready}
        disabled={!ready}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-[14px] font-bold">
          <Icon size={15} strokeWidth={1.75} aria-hidden="true" />
          {label}
        </span>
        <span className="mt-0.5 block text-[12px] leading-snug text-stone">
          {ready ? sub : 'Not set up on this server'}
        </span>
      </span>
      {ready && count !== undefined && (
        <span
          className="text-[15px] font-extrabold tabular-nums"
          title={`${fmtNum(count)} reachable`}
        >
          {fmtNum(count)}
        </span>
      )}
    </label>
  );
}

/** The Android lock screen: the app's notification as the shade shows it. */
function PhonePreview({ title, body }: { title: string; body: string }) {
  return (
    <figure>
      <figcaption className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-stone">
        <Smartphone size={13} aria-hidden="true" /> Android app
      </figcaption>
      <div className="rounded-[18px] bg-ink p-3">
        <div className="rounded-[14px] bg-white/95 p-3 text-ink">
          <div className="flex items-center gap-1.5 text-[11px] text-stone">
            <img src="/brand/logo-48.png" alt="" className="h-4 w-4" />
            GaintPlay · now
          </div>
          <div className="mt-1 line-clamp-1 text-[14px] font-bold">{title || 'Title'}</div>
          <div className="line-clamp-2 text-[13px] leading-snug text-graphite">
            {body || 'Text'}
          </div>
        </div>
      </div>
    </figure>
  );
}

/** A desktop browser's notification. */
function BrowserPreview({ title, body, path }: { title: string; body: string; path: string }) {
  return (
    <figure>
      <figcaption className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-stone">
        <Globe size={13} aria-hidden="true" /> Browser
      </figcaption>
      <div className="flex gap-3 rounded-[12px] border border-hairline bg-paper p-3 shadow-[0_1px_3px_rgba(17,17,16,0.08)]">
        <img src="/brand/logo-48.png" alt="" className="h-9 w-9 shrink-0" />
        <div className="min-w-0">
          <div className="line-clamp-1 text-[13px] font-bold">{title || 'Title'}</div>
          <div className="line-clamp-3 text-[12px] leading-snug text-graphite">
            {body || 'Text'}
          </div>
          <div className="mt-1 truncate text-[11px] text-stone">
            gaintplay.com{path.trim() || '/'}
          </div>
        </div>
      </div>
    </figure>
  );
}

/** The email as the real template renders it (server-side), in a frame that can't run anything. */
function EmailPreview({ kind, email }: { kind: Kind; email: Form['email'] }) {
  const key = useDebounced(JSON.stringify({ kind, email }), 500);
  const preview = useQuery({
    queryKey: ['notify-preview', key],
    queryFn: () =>
      api<{ html: string }>('/notifications/preview', { body: JSON.parse(key) as unknown }),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(560);
  return (
    <figure>
      <figcaption className="mb-1.5 flex items-center gap-1.5 text-[12px] font-semibold text-stone">
        <Mail size={13} aria-hidden="true" /> Preview
        {preview.error && (
          <span className="ml-auto font-semibold text-berry-deep">
            {(preview.error as Error).message}
          </span>
        )}
      </figcaption>
      <div className="overflow-hidden rounded-[12px] border border-hairline bg-canvas">
        {preview.data ? (
          <iframe
            ref={frame}
            title="Email preview"
            sandbox="allow-same-origin"
            srcDoc={preview.data.html}
            className="block w-full"
            style={{ height }}
            onLoad={() => {
              const h = frame.current?.contentDocument?.documentElement.scrollHeight;
              if (h) setHeight(Math.min(1400, h + 8));
            }}
          />
        ) : (
          <div className="h-[320px] animate-pulse bg-ink/[0.04]" />
        )}
      </div>
    </figure>
  );
}

/* ------------------------------------------------------------------ Audience */

function AudienceEditor({
  f,
  setF,
  options,
}: {
  f: Form;
  setF: (fn: (f: Form) => Form) => void;
  options: Options;
}) {
  const mode = (m: Form['mode']) => setF((x) => ({ ...x, mode: m }));
  const filters = f.filters;
  const setFilters = (fs: Filters) => setF((x) => ({ ...x, filters: fs }));
  const active = FILTERS.filter((x) => filters[x.key] !== undefined);
  const free = FILTERS.filter((x) => filters[x.key] === undefined);
  return (
    <div className="space-y-4">
      <div
        className="inline-flex rounded-[12px] bg-ink/[0.06] p-1"
        role="radiogroup"
        aria-label="Who"
      >
        {(
          [
            ['all', 'Everyone'],
            ['filter', 'Filter'],
            ['user', 'One person'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            role="radio"
            aria-checked={f.mode === k}
            onClick={() => mode(k)}
            className={`h-9 rounded-[9px] px-4 text-[14px] font-semibold transition max-sm:h-10 ${
              f.mode === k
                ? 'bg-paper text-ink shadow-[0_1px_0_#e2ddd2,0_1px_3px_rgba(17,17,16,0.08)]'
                : 'text-graphite hover:text-ink'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {f.mode === 'all' && (
        <p className="text-[14px] leading-relaxed text-graphite">
          Every account that can get it on the channels you picked. Sending to everyone needs the
          number typed back, and only one campaign to everyone goes out a day.
        </p>
      )}

      {f.mode === 'user' && (
        <UserPicker
          value={f.user}
          onPick={(u) => setF((x) => ({ ...x, user: u && { id: u.id, label: u.username } }))}
        />
      )}

      {f.mode === 'filter' && (
        <div className="space-y-3">
          {active.length === 0 && (
            <p className="text-[14px] text-graphite">
              No filters yet, so this is everyone. Add one to narrow it down; they all have to
              match.
            </p>
          )}
          {active.map((x, i) => (
            <div key={x.key} className="rounded-[12px] border border-hairline p-3.5">
              <div className="mb-2.5 flex items-center gap-2">
                {i > 0 && (
                  <span className="rounded-full bg-ink/[0.06] px-2 py-0.5 text-[11px] font-bold text-graphite">
                    and
                  </span>
                )}
                <span className="text-[14px] font-bold">{x.label}</span>
                <button
                  type="button"
                  aria-label={`Remove ${x.label}`}
                  onClick={() => {
                    const n = { ...filters };
                    delete n[x.key];
                    setFilters(n);
                  }}
                  className="ml-auto grid h-8 w-8 place-items-center rounded-full text-stone transition hover:bg-canvas hover:text-ink"
                >
                  <X size={15} />
                </button>
              </div>
              <FilterInputs k={x.key} filters={filters} setFilters={setFilters} options={options} />
            </div>
          ))}
          {free.length > 0 && (
            <select
              aria-label="Add a filter"
              className="input h-10 w-auto py-0 pr-8 text-[14px] font-semibold"
              value=""
              onChange={(e) => {
                const k = e.target.value as FilterKey;
                if (k) setFilters({ ...filters, [k]: EMPTY[k] });
              }}
            >
              <option value="">+ Add a filter</option>
              {free.map((x) => (
                <option key={x.key} value={x.key}>
                  {x.label}
                </option>
              ))}
            </select>
          )}
        </div>
      )}
    </div>
  );
}

function FilterInputs({
  k,
  filters,
  setFilters,
  options,
}: {
  k: FilterKey;
  filters: Filters;
  setFilters: (f: Filters) => void;
  options: Options;
}) {
  const put = <K extends FilterKey>(key: K, v: Filters[K]) => setFilters({ ...filters, [key]: v });
  switch (k) {
    case 'countries': {
      const chosen = filters.countries ?? [];
      return (
        <div className="space-y-2">
          {chosen.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {chosen.map((c) => (
                <span key={c} className="chip py-1 pr-1">
                  {country(c)} <span className="text-stone">{c}</span>
                  <button
                    type="button"
                    aria-label={`Remove ${country(c)}`}
                    onClick={() =>
                      put(
                        'countries',
                        chosen.filter((x) => x !== c),
                      )
                    }
                    className="grid h-6 w-6 place-items-center rounded-full hover:bg-canvas"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <select
            aria-label="Add a country"
            className="input h-10 py-0 text-[14px]"
            value=""
            onChange={(e) => e.target.value && put('countries', [...chosen, e.target.value])}
          >
            <option value="">Add a country…</option>
            {options.countries
              .filter((c) => !chosen.includes(c.code))
              .map((c) => (
                <option key={c.code} value={c.code}>
                  {country(c.code)} ({c.code}) · {fmtNum(c.n)}
                </option>
              ))}
          </select>
          <p className="text-[12px] text-stone">
            The account’s country, or where it signed up. Numbers are accounts per country.
          </p>
        </div>
      );
    }
    case 'signedUp':
    case 'cashedOutWhen': {
      const v = filters[k] ?? EMPTY[k];
      return <DatePair v={v} onChange={(d) => put(k, d)} />;
    }
    case 'age': {
      const v = filters.age ?? EMPTY.age;
      return (
        <NumberPair
          v={v}
          onChange={(r) => put('age', r)}
          minLabel="At least (days old)"
          maxLabel="At most (days old)"
        />
      );
    }
    case 'cashouts': {
      const v = filters.cashouts ?? EMPTY.cashouts;
      return (
        <NumberPair
          v={v}
          onChange={(r) => put('cashouts', r)}
          minLabel="At least"
          maxLabel="At most"
          note="Paid cash-outs, all time. 0 to 0 is people who never cashed out."
        />
      );
    }
    case 'cashedOut': {
      const v = filters.cashedOut ?? EMPTY.cashedOut;
      return (
        <NumberPair
          v={v}
          onChange={(r) => put('cashedOut', r)}
          minLabel="At least ($)"
          maxLabel="At most ($)"
          decimal
          note="Paid cash-outs, all time, from the coins spent (1,000 coins = $1)."
        />
      );
    }
    case 'balance': {
      const v = filters.balance ?? EMPTY.balance;
      return (
        <NumberPair
          v={v}
          onChange={(r) => put('balance', r)}
          minLabel="At least (coins)"
          maxLabel="At most (coins)"
          note="Available coins now, not pending ones."
        />
      );
    }
    case 'offer': {
      const v = filters.offer ?? EMPTY.offer;
      return (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block">
              <span className="label">Network</span>
              <select
                className="input h-11 py-0 text-[14px]"
                value={v.providerId}
                onChange={(e) => put('offer', { ...v, providerId: e.target.value })}
              >
                <option value="">Choose…</option>
                {options.providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="label">Offer id (optional)</span>
              <input
                className="input h-11 font-mono text-[14px]"
                value={v.offerId}
                maxLength={100}
                onChange={(e) => put('offer', { ...v, offerId: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="label">Name contains (optional)</span>
              <input
                className="input h-11 text-[14px]"
                value={v.name}
                maxLength={100}
                onChange={(e) => put('offer', { ...v, name: e.target.value })}
                placeholder="Raid"
              />
            </label>
          </div>
          <DatePair v={v} onChange={(d) => put('offer', { ...v, ...d })} />
          <p className="text-[12px] text-stone">
            Credited or still pending; offers the network took back don’t count.
          </p>
        </div>
      );
    }
    case 'hasApp':
    case 'emailConfirmed': {
      const on = filters[k] ?? true;
      const labels =
        k === 'hasApp'
          ? (['Has the app', 'Doesn’t have it'] as const)
          : (['Confirmed', 'Not confirmed'] as const);
      return (
        <div className="inline-flex rounded-[12px] bg-ink/[0.06] p-1" role="radiogroup">
          {[true, false].map((b, i) => (
            <button
              key={String(b)}
              type="button"
              role="radio"
              aria-checked={on === b}
              onClick={() => put(k, b)}
              className={`h-9 rounded-[9px] px-4 text-[14px] font-semibold transition ${
                on === b
                  ? 'bg-paper text-ink shadow-[0_1px_0_#e2ddd2,0_1px_3px_rgba(17,17,16,0.08)]'
                  : 'text-graphite hover:text-ink'
              }`}
            >
              {labels[i]}
            </button>
          ))}
        </div>
      );
    }
  }
}

function DatePair({ v, onChange }: { v: Dates; onChange: (d: Dates) => void }) {
  const id = useId();
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div>
        <label className="label" htmlFor={`${id}-f`}>
          From
        </label>
        <input
          id={`${id}-f`}
          type="date"
          className="input h-11"
          value={v.from}
          max={v.to || undefined}
          onChange={(e) => onChange({ ...v, from: e.target.value })}
        />
      </div>
      <div>
        <label className="label" htmlFor={`${id}-t`}>
          To (including)
        </label>
        <input
          id={`${id}-t`}
          type="date"
          className="input h-11"
          value={v.to}
          min={v.from || undefined}
          onChange={(e) => onChange({ ...v, to: e.target.value })}
        />
      </div>
    </div>
  );
}

function NumberPair({
  v,
  onChange,
  minLabel,
  maxLabel,
  note,
  decimal = false,
}: {
  v: Range;
  onChange: (r: Range) => void;
  minLabel: string;
  maxLabel: string;
  note?: string;
  decimal?: boolean;
}) {
  const id = useId();
  const ok = (s: string) => s === '' || (decimal ? cents(s) !== undefined : whole(s) !== undefined);
  const input = (key: 'min' | 'max', label: string) => (
    <div>
      <label className="label" htmlFor={`${id}-${key}`}>
        {label}
      </label>
      <input
        id={`${id}-${key}`}
        inputMode={decimal ? 'decimal' : 'numeric'}
        className="input h-11 tabular-nums"
        aria-invalid={!ok(v[key]) || undefined}
        value={v[key]}
        onChange={(e) =>
          onChange({ ...v, [key]: e.target.value.replace(decimal ? /[^\d.]/g : /[^\d]/g, '') })
        }
      />
    </div>
  );
  return (
    <div className="space-y-2">
      <div className="grid gap-3 sm:grid-cols-2">
        {input('min', minLabel)}
        {input('max', maxLabel)}
      </div>
      {note && <p className="text-[12px] text-stone">{note}</p>}
    </div>
  );
}

function UserPicker({
  value,
  onPick,
  label = 'Search by username, email or id',
}: {
  value: { id: string; label: string } | null;
  onPick: (u: FoundUser | null) => void;
  label?: string;
}) {
  const [q, setQ] = useState('');
  const term = useDebounced(q.trim(), 300);
  const found = useQuery({
    queryKey: ['notify-users', term],
    queryFn: () =>
      api<{ users: FoundUser[] }>(`/notifications/users?q=${encodeURIComponent(term)}`),
    enabled: term.length >= 2,
  });
  const id = useId();
  if (value)
    return (
      <div className="flex items-center gap-3 rounded-[12px] border border-ink bg-canvas/60 p-3">
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-bold">{value.label}</span>
          <span className="block truncate font-mono text-[12px] text-stone">{value.id}</span>
        </span>
        <button
          type="button"
          className="btn-ghost h-9 px-3 py-0 text-[13px]"
          onClick={() => onPick(null)}
        >
          Change
        </button>
      </div>
    );
  return (
    <div>
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="maya21, maya@example.com or usr_…"
        autoComplete="off"
      />
      {term.length >= 2 && (
        <ul className="mt-2 divide-y divide-hairline overflow-hidden rounded-[12px] border border-hairline bg-paper">
          {found.isFetching && !found.data ? (
            <li className="px-3.5 py-3 text-[13px] text-stone">Searching…</li>
          ) : !found.data?.users.length ? (
            <li className="px-3.5 py-3 text-[13px] text-stone">Nobody matches “{term}”.</li>
          ) : (
            found.data.users.map((u) => {
              const out = u.status === 'deleted' || u.status === 'blocked';
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    disabled={out}
                    onClick={() => onPick(u)}
                    className="flex w-full items-center gap-3 px-3.5 py-2.5 text-left transition hover:bg-canvas disabled:cursor-not-allowed disabled:opacity-55"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-bold">{u.username}</span>
                      <span className="block truncate text-[12px] text-stone">
                        {u.email ?? u.id}
                        {u.email && !u.emailVerified ? ' · not confirmed' : ''}
                      </span>
                    </span>
                    {out && (
                      <Pill tone="berry">{u.status === 'deleted' ? 'Deleted' : 'Blocked'}</Pill>
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Reach and test */

function ReachCard({
  counts,
  samples,
  everyone,
  literal,
  loading,
  error,
  ch,
  kind,
  waiting,
}: {
  counts: Counts | undefined;
  samples: string[];
  /** Counts as a campaign to everyone (lib/audience.ts isBroadcast). */
  everyone: boolean;
  /** The audience is literally everyone, not a filter that reaches nearly all of them. */
  literal: boolean;
  loading: boolean;
  error: unknown;
  ch: Channels;
  kind: Kind;
  waiting: boolean;
}) {
  const skipped = counts ? counts.matched - counts.reachable : 0;
  return (
    <section className="card p-4 sm:p-5" aria-live="polite" aria-busy={loading}>
      <div className="flex items-center gap-2 text-[13px] font-semibold text-stone">
        Who gets it
        {loading && (
          <span className="ml-auto h-3.5 w-3.5 animate-spin rounded-full border-2 border-stone border-r-transparent" />
        )}
      </div>
      {waiting ? (
        <p className="mt-3 text-[14px] text-graphite">Pick the person first.</p>
      ) : error && !counts ? (
        <div className="mt-3">
          <ErrorNote error={error} />
        </div>
      ) : (
        <>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-[40px] font-extrabold leading-none tracking-[-0.03em] tabular-nums">
              {counts ? fmtNum(counts.reachable) : '—'}
            </span>
            <span className="text-[15px] font-semibold text-graphite">
              {counts?.reachable === 1 ? 'person' : 'people'}
            </span>
            {everyone && (
              <span className="ml-auto">
                <Pill tone="ink">{literal ? 'Everyone' : 'Nearly everyone'}</Pill>
              </span>
            )}
          </div>
          {everyone && !literal && (
            <p className="mt-2 text-[13px] leading-snug text-graphite">
              At least half of everyone it could reach, so it counts as a campaign to everyone: the
              number typed back, and one a day.
            </p>
          )}
          <ul className="mt-4 divide-y divide-hairline border-y border-hairline text-[14px]">
            {CHANNELS.map((x) => (
              <li
                key={x.key}
                className={`flex items-center gap-2.5 py-2 ${ch[x.key] ? '' : 'text-stone'}`}
              >
                <x.icon size={15} strokeWidth={1.75} aria-hidden="true" />
                {x.label}
                <span className="ml-auto tabular-nums">
                  {ch[x.key] ? <b>{counts ? fmtNum(counts[x.key]) : '—'}</b> : 'not chosen'}
                </span>
              </li>
            ))}
          </ul>
          {counts && (
            <p className="mt-3 text-[13px] leading-snug text-graphite">
              {fmtNum(counts.matched)} {counts.matched === 1 ? 'account matches' : 'accounts match'}
              .
              {skipped > 0 &&
                ` ${fmtNum(skipped)} can’t get it on these channels${kind === 'promotion' ? ' or didn’t ask for offers' : ''}.`}
            </p>
          )}
          {samples.length > 0 && (
            <p className="mt-2 text-[13px] leading-snug text-stone">
              Including{' '}
              {samples.map((s, i) => (
                <span key={i}>
                  {i > 0 && ', '}
                  <b className="font-semibold text-graphite">{s}</b>
                </span>
              ))}
              {counts && counts.reachable > samples.length ? '…' : '.'}
            </p>
          )}
          <p className="mt-3 text-[12px] leading-snug text-stone">
            Deleted and blocked accounts never get anything. Counted again when it goes out.
          </p>
        </>
      )}
    </section>
  );
}

function TestPanel({
  f,
  me,
  disabled,
  onSend,
}: {
  f: Form;
  me: string | null;
  disabled: boolean;
  onSend: (pushUserId: string | null) => Promise<void>;
}) {
  const push = f.channels.app || f.channels.web;
  const [who, setWho] = useState<{ id: string; label: string } | null>(null);
  const ready = !disabled && (f.channels.email || (push && who));
  return (
    <details className="card group overflow-hidden">
      <summary className="flex min-h-[52px] cursor-pointer list-none items-center gap-2 px-4 py-3 text-[15px] font-bold sm:px-5">
        Send a test to yourself
        <span aria-hidden="true" className="ml-auto text-stone transition group-open:rotate-180">
          ▾
        </span>
      </summary>
      <div className="space-y-3 border-t border-hairline px-4 py-4 sm:px-5">
        {f.channels.email && (
          <p className="text-[13px] text-graphite">
            The email goes to <b className="font-semibold">{me ?? 'your address'}</b>, marked
            [Test].
          </p>
        )}
        {push && (
          <UserPicker
            label="The push goes to this account’s phones and browsers"
            value={who}
            onPick={(u) => setWho(u && { id: u.id, label: u.username })}
          />
        )}
        <button
          type="button"
          className="btn-ghost w-full"
          disabled={!ready}
          onClick={() => void onSend(push && who ? who.id : null)}
        >
          Send test
        </button>
        <p className="text-[12px] leading-snug text-stone">
          Saves the draft first. Not counted, and nobody else gets it.
        </p>
      </div>
    </details>
  );
}

/* ------------------------------------------------------------------ Sent, sending, scheduled */

function Detail({ c, failures, options }: { c: Campaign; failures: Failure[]; options: Options }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const nets = useMemo(() => new Map(options.providers.map((p) => [p.id, p.name])), [options]);
  const done =
    c.sent.app + c.sent.web + c.sent.email + c.failed.app + c.failed.web + c.failed.email;
  const target = (c.recipients.app ?? 0) + (c.recipients.web ?? 0) + (c.recipients.email ?? 0);
  const pct = target ? Math.min(100, Math.round((done / target) * 100)) : 0;
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['notify-campaign', c.id] });
    void qc.invalidateQueries({ queryKey: ['notify-campaigns'] });
  };

  async function cancel() {
    const ok = await confirmAction({
      title:
        c.status === 'scheduled' ? 'Cancel this scheduled campaign?' : 'Stop sending the rest?',
      body:
        c.status === 'scheduled'
          ? 'Nobody gets it. You can duplicate it later to send it after all.'
          : `What already went out stays out (${fmtNum(c.sent.app + c.sent.web + c.sent.email)} so far). Nobody else gets it.`,
      confirmLabel: c.status === 'scheduled' ? 'Cancel it' : 'Stop sending',
      tone: 'danger',
    });
    if (ok === null) return;
    if (await run(() => api(`/notifications/campaigns/${c.id}/cancel`, { body: {} }), 'Cancelled.'))
      refresh();
  }
  // A send that started and stopped carries on where it stopped: same campaign, same records.
  const resumable = (c.status === 'failed' || c.status === 'cancelled') && !!c.startedAt;
  async function resume() {
    const sent = c.sent.app + c.sent.web + c.sent.email;
    const ok = await confirmAction({
      title: 'Send it to the rest?',
      body: `It carries on where it stopped. The ${fmtNum(sent)} already sent aren’t sent again, and anyone who opted out since is skipped.`,
      confirmLabel: 'Resume sending',
    });
    if (ok === null) return;
    if (
      await run(
        () => api(`/notifications/campaigns/${c.id}/resume`, { body: {} }),
        'Sending the rest.',
      )
    )
      refresh();
  }
  async function duplicate() {
    const r = await run(() =>
      api<{ id: string }>(`/notifications/campaigns/${c.id}/duplicate`, { body: {} }),
    );
    if (r) {
      void qc.invalidateQueries({ queryKey: ['notify-campaigns'] });
      void navigate(`/notifications/${r.id}`);
    }
  }

  const line =
    c.status === 'scheduled'
      ? `Goes out ${dateTime(c.scheduledAt!)}, within 5 minutes.`
      : c.status === 'sending'
        ? `Sending since ${dateTime(c.startedAt!)}.`
        : c.status === 'sent'
          ? `Sent ${dateTime(c.startedAt!)}${c.finishedAt ? `, finished ${dateTime(c.finishedAt)}` : ''}.`
          : c.status === 'cancelled'
            ? `Cancelled ${c.cancelledAt ? dateTime(c.cancelledAt) : ''}${c.cancelledBy ? ` by ${c.cancelledBy.name}` : ''}.`
            : (c.error ?? 'Failed.');

  return (
    <div className="space-y-5">
      <BackLink />
      <PageHeader
        eyebrow={
          <span className="flex flex-wrap items-center gap-2">
            <StatusTag s={c.status} /> <KindTag k={c.kind} />
          </span>
        }
        title={c.name}
        description={line}
        actions={
          <>
            {(c.status === 'scheduled' || c.status === 'sending') && (
              <button
                type="button"
                className="btn-ghost h-10 py-0 text-berry-deep"
                onClick={() => void cancel()}
              >
                {c.status === 'scheduled' ? 'Cancel' : 'Stop sending'}
              </button>
            )}
            {resumable && (
              <button type="button" className="btn-primary h-10 py-0" onClick={() => void resume()}>
                <Send size={15} aria-hidden="true" /> Resume
              </button>
            )}
            <button type="button" className="btn-ghost h-10 py-0" onClick={() => void duplicate()}>
              <Copy size={15} aria-hidden="true" /> Duplicate
            </button>
          </>
        }
      />

      {(c.status !== 'scheduled' && c.status !== 'cancelled') || done > 0 ? (
        <Card>
          {c.status === 'sending' && (
            <div className="mb-5">
              <div className="flex items-baseline justify-between text-[13px] font-semibold text-graphite">
                <span>Progress</span>
                <span className="tabular-nums">{pct}%</span>
              </div>
              <div
                className="mt-1.5 h-2 overflow-hidden rounded-full bg-ink/[0.08]"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full rounded-full bg-ink transition-[width] duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}
          <div className="grid gap-4 sm:grid-cols-3">
            {CHANNELS.filter((x) => c.channels[x.key]).map((x) => (
              <div key={x.key}>
                <div className="flex items-center gap-1.5 text-[13px] font-semibold text-stone">
                  <x.icon size={14} strokeWidth={1.75} aria-hidden="true" /> {x.label}
                </div>
                <div className="mt-1.5 text-[28px] font-extrabold leading-none tracking-[-0.025em] tabular-nums">
                  {fmtNum(c.sent[x.key])}
                </div>
                <div className="mt-1.5 text-[13px] text-graphite tabular-nums">
                  of {fmtNum(c.recipients[x.key] ?? 0)}
                  {c.failed[x.key] > 0 && (
                    <span className="font-semibold text-berry-deep">
                      {' '}
                      · {fmtNum(c.failed[x.key])} failed
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
          {c.kind === 'promotion' && c.channels.email && (
            <p className="mt-4 border-t border-hairline pt-3 text-[13px] text-graphite">
              <b className="tabular-nums">{fmtNum(c.unsubscribes)}</b>{' '}
              {c.unsubscribes === 1 ? 'person' : 'people'} unsubscribed from offers through this
              email.
            </p>
          )}
        </Card>
      ) : null}

      {failures.length > 0 && (
        <Card title="Why some failed" pad={false}>
          <ScrollX>
            <table className="data-table table-compact">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Reason</th>
                  <th className="text-right">People</th>
                </tr>
              </thead>
              <tbody>
                {failures.map((x, i) => (
                  <tr key={i}>
                    <td className="whitespace-nowrap">
                      {CHANNELS.find((y) => y.key === x.channel)?.label ?? x.channel}
                    </td>
                    <td className="break-words">
                      {FAILURE_LABEL[x.code] && (
                        <span className="block text-[13px]">{FAILURE_LABEL[x.code]}</span>
                      )}
                      <span className="font-mono text-[12px] text-graphite">{x.code}</span>
                    </td>
                    <td className="text-right tabular-nums">{fmtNum(x.n)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollX>
        </Card>
      )}

      <div className="grid items-start gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <div className="min-w-0 space-y-5">
          {(c.channels.app || c.channels.web) && (
            <Card title="Push notification">
              <div className="grid gap-3 sm:grid-cols-2">
                {c.channels.app && <PhonePreview title={c.push.title} body={c.push.body} />}
                {c.channels.web && (
                  <BrowserPreview title={c.push.title} body={c.push.body} path={c.push.path} />
                )}
              </div>
            </Card>
          )}
          {c.channels.email && (
            <Card title="Email">
              <EmailPreview kind={c.kind} email={c.email} />
            </Card>
          )}
        </div>
        <Card title="Details">
          <KV
            items={[
              ['Type', KIND_LABEL[c.kind]],
              ['Channels', channelNames(c.channels)],
              [
                'Audience',
                `${describeAudience(c.audience, nets)}${c.broadcast && c.audience.kind !== 'all' ? ' (counts as everyone)' : ''}`,
              ],
              ...(c.reason
                ? [['Why it’s a service notice', c.reason] as [ReactNode, ReactNode]]
                : []),
              [
                'Recipients',
                c.recipients.total === null
                  ? 'Counted when it goes out'
                  : people(c.recipients.total),
              ],
              ['Created', `${c.createdBy?.name ?? 'Staff'}, ${dateTime(c.createdAt)}`],
              ...(c.sentBy
                ? [
                    [c.status === 'scheduled' ? 'Scheduled by' : 'Sent by', c.sentBy.name] as [
                      ReactNode,
                      ReactNode,
                    ],
                  ]
                : []),
              ...(c.overrideBy
                ? [
                    [
                      'Owner override',
                      <span className="inline-flex items-center gap-1.5 text-berry-deep">
                        <TriangleAlert size={14} aria-hidden="true" /> {c.overrideBy.name}: second
                        campaign to everyone within a day
                      </span>,
                    ] as [ReactNode, ReactNode],
                  ]
                : []),
              ...(c.error ? [['Error', c.error] as [ReactNode, ReactNode]] : []),
            ]}
          />
        </Card>
      </div>
    </div>
  );
}
